/**
 * Top bar reutilizable para páginas del organizador y de marketing.
 *
 * Layout:  Logo Pagafácil ─── [Soluciones ▾] · Precio · Mis cobros ─── Crear cobro (CTA)
 *
 * Variants:
 *  - default       · fondo paper, full nav (HomePage, MyCobrosPage, EntityPage…)
 *  - compact       · solo logo + acción rápida (OperatorPage, panels donde
 *                    queremos minimizar distracciones pero mantener
 *                    coherencia visual)
 *
 * NO usar en: PublicPage, SharePage, ParticipantPage, CasetaBonoPage,
 * EmbedPage (esos son flujos focus del pagador, sin nav).
 *
 * AdminPage y CasetaAdminPage usan su header rico propio (con título
 * del cobro + status badge) y NO sustituyen este TopBar — añadirían
 * uno arriba si conviene.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';


type Variant = 'default' | 'compact';

interface TopBarProps {
  variant?: Variant;
  /** Texto a la derecha del logo, para contextualizar dónde está el usuario.
   *  Ej. "Mis cobros", "Operador", "Tu oficina". Opcional. */
  contextLabel?: string;
  /** Sustituye al CTA principal "Crear cobro" por otro link. Opcional. */
  rightCta?: { label: string; to: string };
}


export default function TopBar({ variant = 'default', contextLabel, rightCta }: TopBarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="border-b border-line bg-paper sticky top-0 z-30 backdrop-blur supports-[backdrop-filter]:bg-paper/95">
      <div className="max-w-6xl mx-auto px-6 py-3.5 flex items-center justify-between gap-4">
        {/* Brand */}
        <Link to="/" className="flex items-center gap-3 shrink-0" aria-label="Pagafácil · inicio">
          <Wordmark />
          {contextLabel && (
            <>
              <span className="text-ink-400 font-mono text-[11px] hidden sm:inline">·</span>
              <span className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500 truncate max-w-[200px] hidden sm:inline">
                {contextLabel}
              </span>
            </>
          )}
        </Link>

        {/* Nav central (solo variant default · desktop) */}
        {variant === 'default' && (
          <nav className="hidden md:flex items-center gap-5 text-[13.5px] text-ink-700">
            <div className="relative group">
              <button type="button" className="hover:text-ink py-2 inline-flex items-center gap-1">
                Soluciones <span className="text-ink-400 text-[10px]">▾</span>
              </button>
              <div className="absolute left-0 top-full pt-2 hidden group-hover:block z-40 min-w-[220px]">
                <div className="bg-paper border border-line rounded-card shadow-card p-2">
                  <NavSubmark to="/coordina" name="Coordina" desc="Saldo común entre todos" />
                  <NavSubmark to="/propina" name="Propina" desc="QR para propinas en mesa" />
                  <NavSubmark to="/oficina" name="Oficina Fácil" desc="Facturas Verifactu autónomos" />
                </div>
              </div>
            </div>
            <Link to="/empezar" className="hover:text-ink">Empezar guiado</Link>
            <Link to="/precio" className="hover:text-ink">Precio</Link>
            <Link to="/mis-cobros" className="hover:text-ink">Mis cobros</Link>
          </nav>
        )}

        <div className="flex items-center gap-2 shrink-0">
          {/* CTA derecha · siempre visible */}
          <Link
            to={rightCta?.to || '/nuevo'}
            className="px-4 py-2 rounded-pill bg-brand text-paper font-semibold text-[13.5px] hover:bg-brand-700 transition shadow-btn whitespace-nowrap"
          >
            {rightCta?.label || 'Crear cobro'}
          </Link>

          {/* Botón hamburger · solo mobile · solo variant default */}
          {variant === 'default' && (
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={mobileOpen ? 'Cerrar menú' : 'Abrir menú'}
              aria-expanded={mobileOpen}
              className="md:hidden w-10 h-10 rounded-pill border border-line-strong bg-paper text-ink-700 flex items-center justify-center hover:border-brand transition"
            >
              <span aria-hidden className="font-mono text-[16px] leading-none">
                {mobileOpen ? '×' : '☰'}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Mobile menu desplegable */}
      {variant === 'default' && mobileOpen && (
        <div className="md:hidden border-t border-line bg-paper">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500 mb-2">
              · Soluciones
            </div>
            <ul className="mb-4 grid grid-cols-1 gap-1">
              <MobileSubmark to="/coordina" name="Coordina" desc="Saldo común entre todos" onClick={() => setMobileOpen(false)} />
              <MobileSubmark to="/propina" name="Propina" desc="QR para propinas en mesa" onClick={() => setMobileOpen(false)} />
              <MobileSubmark to="/oficina" name="Oficina Fácil" desc="Facturas Verifactu autónomos" onClick={() => setMobileOpen(false)} />
            </ul>
            <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500 mb-2">
              · Producto
            </div>
            <ul className="mb-4 grid grid-cols-1 gap-0.5 text-[14.5px]">
              <li><Link to="/empezar" onClick={() => setMobileOpen(false)} className="block py-1.5 text-ink-700 hover:text-ink">Empezar guiado</Link></li>
              <li><Link to="/nuevo" onClick={() => setMobileOpen(false)} className="block py-1.5 text-ink-700 hover:text-ink">Crear cobro</Link></li>
              <li><Link to="/precio" onClick={() => setMobileOpen(false)} className="block py-1.5 text-ink-700 hover:text-ink">Precio</Link></li>
              <li><Link to="/sorteos-publicos" onClick={() => setMobileOpen(false)} className="block py-1.5 text-ink-700 hover:text-ink">Sorteos abiertos</Link></li>
            </ul>
            <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500 mb-2">
              · Tu cuenta
            </div>
            <ul className="grid grid-cols-1 gap-0.5 text-[14.5px]">
              <li><Link to="/mis-cobros" onClick={() => setMobileOpen(false)} className="block py-1.5 text-ink-700 hover:text-ink">Mis cobros</Link></li>
              <li><Link to="/entidades" onClick={() => setMobileOpen(false)} className="block py-1.5 text-ink-700 hover:text-ink">Mi entidad</Link></li>
              <li><Link to="/partners" onClick={() => setMobileOpen(false)} className="block py-1.5 text-ink-700 hover:text-ink">Partners CRM</Link></li>
            </ul>
          </div>
        </div>
      )}
    </header>
  );
}


function MobileSubmark({ to, name, desc, onClick }: { to: string; name: string; desc: string; onClick: () => void }) {
  return (
    <li>
      <Link to={to} onClick={onClick} className="block py-2 px-3 rounded-lg hover:bg-brand-50">
        <div className="font-display font-semibold text-ink text-[15px]">{name}</div>
        <div className="text-[12px] text-ink-500">{desc}</div>
      </Link>
    </li>
  );
}


function NavSubmark({ to, name, desc }: { to: string; name: string; desc: string }) {
  return (
    <Link to={to} className="block px-3 py-2 rounded-lg hover:bg-brand-50">
      <div className="font-display font-semibold text-ink text-[14px]">{name}</div>
      <div className="text-[11.5px] text-ink-500">{desc}</div>
    </Link>
  );
}


function Wordmark() {
  return (
    <div className="flex items-center gap-2">
      <div
        className="rounded-full w-[22px] h-[22px] shrink-0"
        style={{
          background: 'conic-gradient(#1C5D82 0 33%, #C2683E 0 66%, #5E7B3E 0 100%)',
          boxShadow: 'inset 0 0 0 4px #FBF7F0',
        }}
        aria-hidden
      />
      <span className="font-display font-bold text-[17px] text-ink tracking-[-0.01em] leading-none">
        Pagafácil
      </span>
    </div>
  );
}
